// src/hooks/useDeliveryFeeConfig.ts
import { useEffect, useState } from 'react';
import {
  subscribeToRateCard,
  calculateDeliveryFee,
  resolveStandardDeliveryFee,
} from '../services/rateCard.service';
import { RateCardConfig } from '../types/index';

interface DeliveryFeeInfo {
  deliveryFee: number;
  threshold: number;
  rateConfig: RateCardConfig | null;
  loading: boolean;
  error: string | null;
  calculationMethod: string;
  getDeliveryFee: (orderValue: number, count?: number, isCommunity?: boolean) => number;
}

const DEFAULT_FEE = 40;
const DEFAULT_THRESHOLD = 500;

/**
 * Hook to fetch delivery fee based on:
 * 1. Order Value (Free delivery threshold)
 * 2. Location (Community vs External)
 * 3. Item Count (Small vs Large orders within community)
 */
export const useDeliveryFeeConfig = (
  orderValue: number = 0,
  itemCount: number = 0,
  isCommunityOrder: boolean = false
): DeliveryFeeInfo => {
  const [rateConfig, setRateConfig] = useState<RateCardConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deliveryFee, setDeliveryFee] = useState<number>(DEFAULT_FEE);
  const [calculationMethod, setCalculationMethod] = useState<string>("Loading...");

  useEffect(() => {
    setLoading(true);

    const unsubscribe = subscribeToRateCard((config: RateCardConfig) => {
      try {
        setRateConfig(config);
        setError(null);
        setLoading(false);
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Unknown error';
        console.error('Error processing rate config:', err);
        setError(errorMsg);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!rateConfig) return;

    const { fee, method } = calculateFeeInternal(
      orderValue,
      itemCount,
      isCommunityOrder,
      rateConfig
    );
    setDeliveryFee(fee);
    setCalculationMethod(method);
  }, [rateConfig, orderValue, itemCount, isCommunityOrder]);

  const calculateFeeInternal = (
    val: number,
    count: number,
    isCommunity: boolean,
    config: RateCardConfig
  ): { fee: number; method: string } => {
    const threshold = config.deliveryThreshold ?? 0;

    // 1. FREE / ABOVE-THRESHOLD DELIVERY
    if (threshold > 0 && val >= threshold) {
      const fee = config.deliveryFeeAboveThreshold ?? 0;
      return {
        fee,
        method:
          fee === 0
            ? `Free Delivery (Order ≥ ₹${threshold})`
            : `Reduced delivery (Order ≥ ₹${threshold})`,
      };
    }

    // 2. COMMUNITY ORDER LOGIC
    if (isCommunity) {
      const itemThreshold = config.communityItemThreshold ?? 3;

      if (count <= itemThreshold) {
        return {
          fee: config.communitySmallOrderFee ?? 10,
          method: `Community Small (≤${itemThreshold} items)`,
        };
      }
      return {
        fee: config.communityLargeOrderFee ?? 20,
        method: `Community Large (>${itemThreshold} items)`,
      };
    }

    // 3. EXTERNAL / STANDARD DELIVERY (matches Rate Card base fee)
    const standardFee = resolveStandardDeliveryFee(config);
    return {
      fee: standardFee,
      method: standardFee === 0 ? 'Free Standard Delivery' : 'Standard External Delivery',
    };
  };

  const getDeliveryFee = (
    val: number,
    count: number = 0,
    isCommunity: boolean = false
  ): number => {
    if (!rateConfig) return DEFAULT_FEE;
    if (!isCommunity) {
      return calculateDeliveryFee(val, rateConfig);
    }
    return calculateFeeInternal(val, count, isCommunity, rateConfig).fee;
  };

  return {
    deliveryFee,
    threshold: rateConfig?.deliveryThreshold ?? DEFAULT_THRESHOLD,
    rateConfig,
    loading,
    error,
    calculationMethod,
    getDeliveryFee,
  };
};
